/**
 * API Client Configuration
 * 
 * Single Responsibility Principle - Gerencia apenas a configuração do axios
 * Dependency Inversion Principle - Usa configuração centralizada
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { environment } from '../../config/environment';

/**
 * Configuração do cliente HTTP
 */
export class APIConfig {
  private static instance: AxiosInstance;

  /**
   * Retorna instância singleton do axios configurado
   */
  public static getInstance(): AxiosInstance {
    if (!APIConfig.instance) {
      APIConfig.instance = axios.create({
        baseURL: environment.apiBaseUrl,
        timeout: environment.apiTimeout,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Interceptor para requests
      APIConfig.instance.interceptors.request.use(
        (config) => {
          // Adicionar token JWT se existir (chave correta: awflux_token)
          const token = localStorage.getItem('awflux_token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }

          // Enviar organização ativa para usuários de grupo
          const activeOrgId = localStorage.getItem('awflux_active_organization_id');
          if (activeOrgId) {
            config.headers['X-Organization-Id'] = activeOrgId;
          }
          
          return config;
        },
        (error) => {
          console.error('[API Request Error]', error);
          return Promise.reject(error);
        }
      );

      // Interceptor para responses
      APIConfig.instance.interceptors.response.use(
        (response) => {
          return response;
        },
        (error: AxiosError) => {
          console.error('[API Response Error]', {
            status: error.response?.status,
            message: error.message,
            url: error.config?.url,
          });

          // Se receber 401 Unauthorized, limpar token e redirecionar para login
          if (error.response?.status === 401) {
            console.warn('[API] Token inválido ou expirado - redirecionando para login');
            localStorage.removeItem('awflux_token');
            localStorage.removeItem('awflux_user');
            localStorage.removeItem('awflux_organization');
            window.location.href = '/login';
          }

          return Promise.reject(error);
        }
      );
    }

    return APIConfig.instance;
  }

  /**
   * Atualiza a base URL da API
   */
  public static setBaseURL(baseURL: string): void {
    APIConfig.getInstance().defaults.baseURL = baseURL;
  }

  /**
   * Atualiza o timeout
   */
  public static setTimeout(timeout: number): void {
    APIConfig.getInstance().defaults.timeout = timeout;
  }
}
